import {
  AfterViewInit,
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component, ElementRef,
  HostListener,
  Input, OnInit,
  ViewChild
} from '@angular/core';
import { Category } from '~app/modules/generic/models';
import { NUMBER_0, NUMBER_1000, NUMBER_1200, NUMBER_16, NUMBER_260, NUMBER_500, NUMBER_600 } from '~shared/constants';
import { ApplicationEventType, CarouselActionType } from '~shared/modules/application-event/constants';
import { GaTrackingEventLabel, GATrackingEventType } from '~shared/modules/ga-tracking/constants';
import { GtmDatalayerService } from '~shared/services/gtm-datalayer.service';
import { select, Store } from '@ngrx/store';
import { getCategory } from '~app/modules/generic/store';
import { CatalogState } from '~app/modules/catalog/store';
import { debounce } from '~shared/decorators/debounce.decorator';

@Component({
  selector: 'app-related-categories-grid',
  templateUrl: './related-categories-grid.component.html',
  styleUrls: ['./related-categories-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class RelatedCategoriesGridComponent implements AfterViewInit, OnInit {
  @Input() categories: Category[];
  @Input() isMobile: boolean;

  @ViewChild('categoryCardsCarousel') categoryCarousel: ElementRef;

  scrollLeft = NUMBER_0;
  arrowEnabled = true;
  showLeftArrow = false;
  showRightArrow = true;
  parentCategory: Category

  get leftArrowVisible(): boolean {
    return this.canScrollLeft() && !this.isMobile;
  }

  get rightArrowVisible(): boolean {
    return this.canScrollRight() && !this.isMobile;
  }

  protected readonly GaTrackingEventLabel = GaTrackingEventLabel;

  constructor(private cd: ChangeDetectorRef, private gtmDatalayerService: GtmDatalayerService, private store$: Store<CatalogState>) {}

  ngOnInit(): void {
    this.store$.pipe(select(getCategory)).subscribe((category) => {
      this.parentCategory = category;
    })
  }

  ngAfterViewInit(): void {
    this.updateArrowsVisibility();
    this.cd.detectChanges();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    const currentScrollLeft = this.categoryCarousel.nativeElement.scrollLeft;
    this.updateArrowsVisibility();
    this.categoryCarousel.nativeElement.scrollLeft = currentScrollLeft;
    this.scrollLeft = currentScrollLeft;
  }

  getScrollLeft(): number {
    const carouselWidth = this.categoryCarousel?.nativeElement?.offsetWidth;
    const cardWidth = NUMBER_260;
    const padding = NUMBER_16;
    let categoriesBySwipe = 6;
    if (carouselWidth <= NUMBER_1000) {
      categoriesBySwipe = 3;
    } else if (carouselWidth <= NUMBER_1200) {
      categoriesBySwipe = 5;
    }

    return (cardWidth + padding) * categoriesBySwipe;
  }

  clickRightArrow(): void {
    if (this.arrowEnabled) {
      this.arrowEnabled = false;
      const scrollAmount = this.getScrollLeft();
      this.scrollLeft = Math.min(
        this.scrollLeft + scrollAmount,
        this.categoryCarousel.nativeElement.scrollWidth - this.categoryCarousel.nativeElement.clientWidth
      );
      this.categoryCarousel.nativeElement.scrollLeft = this.scrollLeft;
      setTimeout(() => {
        this.arrowEnabled = true;
        this.updateArrowsVisibility();
      }, NUMBER_500);
    }
  }

  clickLeftArrow(): void {
    if (this.arrowEnabled) {
      this.arrowEnabled = false;
      const scrollAmount = this.getScrollLeft();
      this.scrollLeft = Math.max(this.scrollLeft - scrollAmount, NUMBER_0);
      this.categoryCarousel.nativeElement.scrollLeft = this.scrollLeft;
      setTimeout(() => {
        this.arrowEnabled = true;
        this.updateArrowsVisibility();
      }, NUMBER_500);
    }
  }

  @debounce()
  updateArrowsVisibility(): void {
    const carousel = this.categoryCarousel.nativeElement;
    const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;
    this.scrollLeft = carousel.scrollLeft;
    this.showLeftArrow = !this.isMobile && this.scrollLeft > NUMBER_0;
    this.showRightArrow = !this.isMobile && this.scrollLeft < maxScrollLeft;

    this.cd.detectChanges();
  }

  canScrollRight(): boolean {
    return (
      this.categoryCarousel?.nativeElement?.scrollWidth >
      this.categoryCarousel?.nativeElement?.offsetWidth + this.scrollLeft
    );
  }

  canScrollLeft(): boolean {
    return this.scrollLeft > NUMBER_0 ;
  }

  onRelatedCategoriesCarouselNavigation(): void {
      this.gtmDatalayerService.gtagEvent({
        event: GATrackingEventType.SE_RELATED_CATEGORIES_SCROLL,
        category: ApplicationEventType.Interaction,
        action: CarouselActionType.ContentCarouselButton
      });
  }
}
